import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface Child {
  id: string;
  name: string;
  grade: string;
  class: string;
  lastActive: string;
  progress: number;
  isOnline?: boolean;
}

interface Activity {
  id: string;
  childId: string;
  childName: string;
  type: string;
  title: string;
  score?: number;
  completed: boolean;
  date: string;
}

const ParentDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedChild, setSelectedChild] = useState<string | null>(null);

  // Fetch children and their activities
  useEffect(() => {
    const fetchChildrenAndActivities = async () => {
      if (!userData?.uid) return;

      try {
        setLoading(true);
        // Fetch children linked to this parent
        const childrenQuery = query(
          collection(db, 'students'),
          where('parentId', '==', userData.uid)
        );
        const childrenSnapshot = await getDocs(childrenQuery);
        
        const fetchedChildren: Child[] = childrenSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          name: doc.data().name || 'Unnamed Child',
          grade: doc.data().grade || '1',
          class: doc.data().class || 'A',
          lastActive: doc.data().lastActive || 'Never',
          progress: doc.data().progress || 0,
          isOnline: Math.random() > 0.5 // Simulating online status for demo
        } as Child));
        
        setChildren(fetchedChildren);
        
        // Set the first child as selected by default
        if (fetchedChildren.length > 0 && !selectedChild) {
          setSelectedChild(fetchedChildren[0].id);
        }
        
        // Fetch recent activities for all children
        const activitiesArray: Activity[] = [];
        for (const child of fetchedChildren) {
          const activitiesQuery = query(
            collection(db, 'activities'),
            where('childId', '==', child.id)
          );
          
          try {
            const activitiesSnapshot = await getDocs(activitiesQuery);
            const childActivities = activitiesSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              childName: child.name
            } as Activity));
            
            activitiesArray.push(...childActivities);
          } catch (err) {
            console.error(`Error fetching activities for child ${child.id}:`, err);
          }
        }
        
        // If no activities found, create mock data for demo
        if (activitiesArray.length === 0 && fetchedChildren.length > 0) {
          const mockActivities: Activity[] = createMockActivities(fetchedChildren);
          setActivities(mockActivities);
        } else {
          setActivities(activitiesArray);
        }
      } catch (err) {
        console.error('Error fetching children data:', err);
        setError('Failed to load children data');
      } finally {
        setLoading(false);
      }
    };

    fetchChildrenAndActivities();
  }, [userData?.uid, selectedChild]);

  // Create mock activities for demo purposes
  const createMockActivities = (children: Child[]): Activity[] => {
    const activities: Activity[] = [];
    const activityTypes = ['quiz', 'homework', 'lesson', 'practice'];
    const subjects = ['Mathematics', 'Hungarian', 'Science', 'English'];
    
    // Create activities for each child
    children.forEach(child => {
      // Generate 5 random activities per child
      for (let i = 0; i < 5; i++) {
        const type = activityTypes[Math.floor(Math.random() * activityTypes.length)];
        const subject = subjects[Math.floor(Math.random() * subjects.length)];
        const completed = Math.random() > 0.3;
        const score = completed ? Math.floor(Math.random() * 30) + 70 : undefined;
        
        // Random date within the last 7 days
        const date = new Date();
        date.setDate(date.getDate() - Math.floor(Math.random() * 7));
        
        activities.push({
          id: `mock-${child.id}-${i}`,
          childId: child.id,
          childName: child.name,
          type,
          title: `${subject} ${type}`,
          score,
          completed,
          date: date.toISOString()
        });
      }
    });
    
    // Sort by date (most recent first)
    return activities.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  // Filter activities for the selected child
  const filteredActivities = selectedChild 
    ? activities.filter(activity => activity.childId === selectedChild)
    : activities;

  // Sort activities by date (most recent first)
  const sortedActivities = [...filteredActivities].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Find the selected child's data
  const selectedChildData = selectedChild 
    ? children.find(child => child.id === selectedChild) 
    : null;

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `${t('parent.today')}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `${t('parent.yesterday')}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Display loading state
  if (loading && children.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#309D97]"></div>
      </div>
    );
  }

  // Display error message
  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  // Display message when no children are found
  if (children.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-xl font-semibold mb-4">{t('parent.noChildren')}</h2>
        <p className="text-gray-500">{t('parent.contactAdmin')}</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('parent.childrenProgress')}</h1>
      
      {/* Children selector tabs */}
      <div className="bg-white rounded-lg shadow-lg mb-6">
        <div className="flex overflow-x-auto border-b">
          {children.map(child => (
            <button
              key={child.id}
              className={`px-4 py-3 whitespace-nowrap border-b-2 ${
                selectedChild === child.id 
                  ? 'border-[#309D97] text-[#309D97] font-medium' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setSelectedChild(child.id)}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#309D97] bg-opacity-20 flex items-center justify-center text-[#309D97] font-medium mr-2">
                  {child.name.charAt(0)}
                </div>
                <div>
                  <span className="block">{child.name}</span>
                  <span className="text-xs block">{t('students.gradeNumber', { number: child.grade })}, {t('students.classLetter', { letter: child.class })}</span>
                </div>
                {child.isOnline && (
                  <span className="ml-2 h-2 w-2 rounded-full bg-green-500"></span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
      
      {/* Selected child details */}
      {selectedChildData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Child overview card */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-[#309D97] bg-opacity-20 flex items-center justify-center text-[#309D97] font-medium mr-3">
                {selectedChildData.name.charAt(0)}
              </div>
              <div>
                <h3 className="text-lg font-medium">{selectedChildData.name}</h3>
                <p className="text-sm text-gray-500">
                  {t('students.gradeNumber', { number: selectedChildData.grade })}, {t('students.classLetter', { letter: selectedChildData.class })}
                </p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>{t('parent.overallProgress')}</span>
                  <span className="font-medium">{selectedChildData.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#309D97] h-2 rounded-full" 
                    style={{ width: `${selectedChildData.progress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="flex justify-between text-sm">
                <span>{t('parent.lastActive')}</span>
                <span className="font-medium">{selectedChildData.isOnline ? t('parent.online') : selectedChildData.lastActive}</span>
              </div>
            </div>
          </div>
          
          {/* Subject progress cards */}
          <div className="bg-white rounded-lg shadow-lg p-6 md:col-span-2">
            <h3 className="text-lg font-medium mb-4">{t('parent.subjectProgress')}</h3>
            
            {/* Mock subject progress for demo */}
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">{t('content.subjects.Mathematics')}</span>
                  <span className="text-sm text-gray-500">85%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">{t('content.subjects.Hungarian')}</span>
                  <span className="text-sm text-gray-500">72%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: '72%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">{t('content.subjects.Science')}</span>
                  <span className="text-sm text-gray-500">64%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '64%' }}></div>
                </div>
              </div>
              
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">{t('content.subjects.English')}</span>
                  <span className="text-sm text-gray-500">93%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: '93%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Recent activities */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h3 className="text-lg font-medium mb-4">
          {selectedChild 
            ? t('parent.childActivities', { name: selectedChildData?.name || '' }) 
            : t('parent.allChildrenActivities')}
        </h3>
        
        {sortedActivities.length > 0 ? (
          <div className="divide-y">
            {sortedActivities.map(activity => (
              <div key={activity.id} className="py-3">
                <div className="flex justify-between mb-1">
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white ${
                      activity.type === 'quiz' ? 'bg-purple-500' :
                      activity.type === 'homework' ? 'bg-blue-500' :
                      activity.type === 'lesson' ? 'bg-green-500' : 'bg-yellow-500'
                    } mr-3`}>
                      {activity.type.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">
                        {activity.title}
                        {!selectedChild && ` - ${activity.childName}`}
                      </p>
                      <p className="text-sm text-gray-500">
                        {activity.completed 
                          ? `${t('parent.completed')}${activity.score ? ` - ${activity.score}%` : ''}` 
                          : t('parent.inProgress')}
                      </p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">{formatDate(activity.date)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">{t('parent.noActivities')}</p>
        )}
      </div>
      
      {/* Time management section */}
      <div className="bg-white rounded-lg shadow-lg p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">{t('parent.timeManagement')}</h3>
          <button className="text-[#309D97] hover:text-[#1e7d77] font-medium">
            {t('parent.setLimits')}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-2">{t('parent.dailyUsage')}</h4>
            <div className="flex justify-between mb-1">
              <span className="text-sm">{t('parent.limit')}</span>
              <span className="text-sm font-medium">120 {t('parent.minutes')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">{t('parent.used')}</span>
              <span className="text-sm font-medium">45 {t('parent.minutes')}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-[#309D97] h-2 rounded-full" style={{ width: '37.5%' }}></div>
            </div>
          </div>
          
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-2">{t('parent.weeklyUsage')}</h4>
            <div className="flex justify-between mb-1">
              <span className="text-sm">{t('parent.limit')}</span>
              <span className="text-sm font-medium">600 {t('parent.minutes')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">{t('parent.used')}</span>
              <span className="text-sm font-medium">320 {t('parent.minutes')}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div className="bg-[#309D97] h-2 rounded-full" style={{ width: '53.3%' }}></div>
            </div>
          </div>
          
          <div className="border rounded-lg p-4">
            <h4 className="font-medium mb-2">{t('parent.activeHours')}</h4>
            <div className="text-sm">
              <p>{t('parent.schoolDays')}: 15:00 - 19:00</p>
              <p>{t('parent.weekends')}: 10:00 - 20:00</p>
            </div>
            <button className="mt-2 text-sm text-[#309D97] hover:text-[#1e7d77]">
              {t('common.edit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard; 